import { createContext, useContext } from "react";
import type { HTTP } from "@/core/http";
import type { AuthService } from "@/modules/auth/services/authService";
import type { RiskService } from "@/modules/risk/services/riskService";
import type { ChatService } from "@/modules/chat/services/chatService";
import type { IssueService } from "@/modules/issue/services/issueService";
import type { BusinessRequirementsService } from "@/modules/proposal-management/business-requirements/services/businessRequirementsService";
import type { BusinessRequirementEstimatesService } from "@/modules/proposal-management/business-requirement-estimates/services/businessRequirementEstimatesService";
import type { RfpAnalysisService } from "@/modules/proposal-management/rfp-analysis/services/rfpAnalysisService";
import type { RomEffortEstimatesService } from "@/modules/proposal-management/rom-effort-estimates/services/romEffortEstimatesService";
import type { UserService } from "@/modules/admin/services/userService";
import type { RoleService } from "@/modules/admin/services/roleService";
import type { SkillService } from "@/modules/admin/services/skillService";
import type { ProjectService } from "@/modules/project/services/projectService";

export interface ServiceMap {
  auth: AuthService;
  risk: RiskService;
  chat: ChatService;
  issue: IssueService;
  businessRequirements: BusinessRequirementsService;
  businessRequirementEstimates: BusinessRequirementEstimatesService;
  rfpAnalysis: RfpAnalysisService;
  romEffortEstimates: RomEffortEstimatesService;
  adminUsers: UserService;
  adminRoles: RoleService;
  adminSkills: SkillService;
  project: ProjectService;
}

interface ServiceLocatorContextValue {
  services: ServiceMap;
  httpClient: HTTP;
}

const ServiceLocatorContext = createContext<ServiceLocatorContextValue | null>(
  null
);

export function useServiceLocator(): ServiceLocatorContextValue {
  const context = useContext(ServiceLocatorContext);
  if (!context) {
    throw new Error(
      "useServiceLocator must be used within ServiceLocatorProvider"
    );
  }
  return context;
}

export { ServiceLocatorContext };
